<template>
  <div class="flex flex-col gap-12">
    <FormStepInputs
      :step="{
        stepName: name,
        formInputs: content,
      }"
    >
      <template #paymentInfo>
        <div id="paymentDetails">
          <ReviewSummary
            :reviewSummaries="mapPaymentItems"
            :isConfirmationStep="false"
            :disableExpandToggle="false"
            :showDiscounts="true"
            :title="paymentInfoSummary.title"
            :isSelfService="true"
            :heading="paymentInfoSummary.heading"
            :belowHeading="paymentInfoSummary.belowHeading"
            :subheading="paymentInfoSummary.subheading"
          />
        </div>
      </template>
      <template #directDebitSettings>
        <div v-if="paymentConnectedDirectDebit">
          <ReviewSummary
            :reviewSummaries="directDebitSettingsSummaries"
            :isConfirmationStep="false"
            :disableExpandToggle="false"
            :showDiscounts="true"
            :title="directDebitSettingsSummary.title"
            :isSelfService="true"
            :heading="directDebitSettingsSummary.heading"
            :belowHeading="directDebitSettingsSummary.belowHeading"
            :subheading="directDebitSettingsSummary.subheading"
          />
        </div>
      </template>
      <template #relevantPaymentInfoBox>
        <div class="racq-max-w-xl5">
          <InformationBox
            :description="relevantPaymentInfoBox?.content?.value"
            :type="relevantPaymentInfoBox.messageType?.value"
          />
        </div>
      </template>
    </FormStepInputs>
  </div>
</template>
<script setup lang="ts">
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import { IReviewSummary, ISitecoreFormGenericInput, OwnedAssistanceProduct } from '@/interfaces';
import { useManageDirectDebitStore, useAccountStore } from '@/store';
import { getFormInput } from '@/utils/form-input-helper';
import { onMounted, ref } from 'vue';
import ReviewSummary from '@/components/ReviewSummary/ReviewSummary.vue';
import { PaymentMethod } from '@/interfaces/self-service/defer-payment.interface';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { executeDisplayRulesNoPolicy } from '@/utils/display-rules/execute-rules';
import { RSADirectDebitDisplayRules } from '@/utils/self-service-rules/RSADisplayRulesDirectDebit';
import { ApttusDirectDebitStatus } from '@/interfaces/entities/apttus-direct-debit-status-response.interface';
import { computed } from 'vue';
import { CardTypes } from '@/enums/payment-types.enum';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { useToastAlertStore } from '@/store/toast-alert/toast-alert.state';
import { ToastAlertType } from '@/enums/toast-alert-enum';
import { LocationQueryValue, useRoute, useRouter } from 'vue-router';
import { toMaskedCardNumber } from '@/utils/convert';
import { UpdateType } from '@/enums/direct-debit.enum';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import { useI18n } from 'vue-i18n';
import { LIFESTYLE_FORDNCO, RoadsideProductType, RSA_ROADSIDE_EXCESS_USE } from '@/utils/roadside-helpers';

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  name: string;
}>();

const manageDirectDebitStore = useManageDirectDebitStore();
const accountStore = useAccountStore();
const store = useToastAlertStore();
const route = useRoute();
const router = useRouter();

const paymentInfoSummary = getFormInput(props.content, 'paymentInfo');
const directDebitSettingsSummary = getFormInput(props.content, 'directDebitSettings');
const relevantPaymentInfoBox = getFormInput(props.content, 'relevantPaymentInfoBox');
const paymentAndDirectDebitUpdateToastMsg = getFormInput(props.content, 'paymentAndDirectDebitUpdateToast');
const directDebitSettingsUpdateToastMsg = getFormInput(props.content, 'directDebitSettingsUpdateToast');
const paymentUpdateToastToastMsg = getFormInput(props.content, 'paymentUpdateToast');
const directDebitSettingsSummaries = ref<IReviewSummary[]>([]);
const directDebitStatus = ref<ApttusDirectDebitStatus>();
const dictionary = useI18n().getLocaleMessage('en') as Record<string, string>;

enum Bookmarks {
  paymentDetails = 'paymentDetails',
}

const paymentConnectedDirectDebit = computed(() => {
  return directDebitStatus.value === ApttusDirectDebitStatus.Current;
});

onMounted(async () => {
  await initializeDirectDebitDetails();
  pushAnalytics();

  const updateType = route.query.updated;
  if (updateType) {
    await handleUpdateFlow(updateType);
  }
});

async function initializeDirectDebitDetails() {
  await manageDirectDebitStore.setDirectDebitDetailsFromIdentity();
  directDebitSettingsSummaries.value = await mapDirectDebitSummaryItems(directDebitSettingsSummary.fields);
  directDebitStatus.value = manageDirectDebitStore.directDebitStatus;
}

async function handleUpdateFlow(rawUpdateType: LocationQueryValue | LocationQueryValue[] | undefined) {
  const updateType = Array.isArray(rawUpdateType) ? rawUpdateType[0] : rawUpdateType;

  if (!updateType || !Object.values(UpdateType).includes(updateType as UpdateType)) {
    return; // Invalid or missing update type — skip toast
  }

  await refreshDirectDebitSummaries();
  clearUpdateQueryParam();
  showUpdateToast(updateType as UpdateType);
}

async function refreshDirectDebitSummaries() {
  directDebitSettingsSummaries.value = await mapDirectDebitSummaryItems(directDebitSettingsSummary.fields);
  directDebitStatus.value = manageDirectDebitStore.directDebitStatus;
}

function clearUpdateQueryParam() {
  setTimeout(() => {
    router.replace({ query: { ...route.query, updated: undefined } });
  }, 10);
}

function showUpdateToast(updateType: UpdateType) {
  const toastMessages = {
    [UpdateType.PaymentAndDirectDebit]: paymentAndDirectDebitUpdateToastMsg?.label?.value,
    [UpdateType.DirectDebit]: directDebitSettingsUpdateToastMsg?.label?.value,
    [UpdateType.Payment]: paymentUpdateToastToastMsg?.label?.value,
  };

  const description = toastMessages[updateType] || 'Update successful';

  store.addToastAlert({
    type: ToastAlertType.SUCCESS,
    title: '',
    description,
    duration: 5000,
  });
}

const mapPaymentItems = computed(() => {
  const summaries = paymentInfoSummary.fields ?? [];

  return summaries.map((summary: any) => ({
    ...summary,
    items: summary.items
      .filter((item: any) =>
        executeDisplayRulesNoPolicy(RSADirectDebitDisplayRules, item.fields.displayRules, directDebitStatus.value!)
      )
      .map((item: any) => ({
        ...item,
        fields: {
          ...item.fields,
          content: { value: getPaymentInfoPaymentDetails(item.fields.content) },
        },

        showPrimaryLink: true,
      })),
  }));
});

const mapDirectDebitSummaryItems = async (summaries: any) => {
  await accountStore.fetchIdentityDetails(true);
  const eligibleDDProducts = await manageDirectDebitStore.getPortfolioAssitanceProductsForDirectDebit();
  const resolved = await Promise.all(
    summaries.map(async (summary: any) => {
      const items = mapAssistanceProductsToReviewSummaries(eligibleDDProducts, summary);
      return { ...summary, items };
    })
  );
  return resolved;
};

function mapAssistanceProductsToReviewSummaries(
  assistanceProducts: OwnedAssistanceProduct[] = [],
  summary: any = null
) {
  const summaryItem = summary && summary?.items ? summary.items.at(0) : undefined;
  const primaryLinkField = summaryItem?.fields?.primaryLink?.value;
  return assistanceProducts
    .filter(
      (product) =>
        product?.roadsidePolicyType !== RoadsideProductType.Cmo &&
        product?.productCode !== RSA_ROADSIDE_EXCESS_USE &&
        product?.productCode !== LIFESTYLE_FORDNCO
    )
    .map((product, index) => ({
      name: product.productCode,
      title: {
        value: product.vehicleDetail
          ? `${product.vehicleDetail.manufacturingYear} ${product.vehicleDetail.make.description} ${product.vehicleDetail.model.description} (${product.vehicleDetail.registrationNumber.toUpperCase()})`
          : '',
      },
      content: {
        value: product.tierDescription,
      },
      subcontent: {
        value: `Vehicle: ${product.vehicleDetail?.make ?? 'N/A'} ${product.vehicleDetail?.model ?? ''}`,
      },
      icon: { value: product.isAutoPayment ? 'bg-icon-success' : 'bg-icon-danger-dark-grey' },
      showItem: true,
      fields: {
        primaryLink: primaryLinkField
          ? { value: { href: primaryLinkField.href, text: primaryLinkField.text } }
          : undefined,
      },
    }));
}

function getPaymentInfoPaymentDetails(content: Field<string>): string {
  const bankIcon = `<span class="bg-icon-bank-account icon w-3 h-3 bg-no-repeat bg-contain inline-block"></span>`;
  const mastercardIcon = `<span class="bg-icon-mastercard icon w-6 h-5 bg-no-repeat bg-contain inline-block"></span>`;
  const visaIcon = `<span class="bg-icon-visa icon w-3 h-3 bg-no-repeat bg-contain inline-block"></span>`;
  const amexIcon = `<span class="bg-icon-amex icon w-3 h-3 bg-no-repeat bg-contain inline-block"></span>`;

  let paymentDetailsText = '';

  if (manageDirectDebitStore?.directDebitPaymentMethod === PaymentMethod.BankAccount) {
    // Bank direct debit
    paymentDetailsText = `${bankIcon} Bank account ${manageDirectDebitStore.apttusDirectDebitStatusResponse?.bank?.bsb} - ${manageDirectDebitStore.apttusDirectDebitStatusResponse?.bank?.accountNumber}`;
  } else {
    // Card direct debit
    const cardType = manageDirectDebitStore.apttusDirectDebitStatusResponse?.creditCard?.cardType;
    let cardIcon = '';
    if (cardType?.toLowerCase() === CardTypes.MasterCard) {
      cardIcon = mastercardIcon;
    } else if (cardType?.toLowerCase() === CardTypes.Visa) {
      cardIcon = visaIcon;
    } else if (cardType?.toLowerCase() === CardTypes.Amex) {
      cardIcon = amexIcon;
    } else {
      // Edge case
      cardIcon = bankIcon;
    }
    paymentDetailsText = `<div class="flex items-center gap-[6px]">${cardIcon} ${cardType} ${toMaskedCardNumber(manageDirectDebitStore.apttusDirectDebitStatusResponse?.creditCard?.maskedCardNumber, 16)} - ${manageDirectDebitStore.apttusDirectDebitStatusResponse?.creditCard?.cardExpiry}</div>`;
  }

  return replaceBookmark(Bookmarks.paymentDetails, content.value, paymentDetailsText.replaceAll('"', "'"));
}

const pushAnalytics = () => {
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.populateProducts(dictionary);
  pageAnalytics.pushAnalytics();
};
</script>
